import { createContext, useContext, useState, type ReactNode } from 'react';

export interface CallStats {
  duration: number; // ms
  audioSentBytes: number;
  videoSentBytes: number;
  packetLoss: number; // percentage
  latency: number; // ms
  resolution: { width: number; height: number };
}

interface MediaContextType {
  isMicOn: boolean;
  isVideoOn: boolean;
  displayName: string;
  selectedAudioDeviceId: string;
  selectedVideoDeviceId: string;
  lastCallStats: CallStats | null; // NEW
  email: string; // NEW
  role: 'host' | 'participant' | 'interviewer' | null; // NEW
  interviewId: string | null; // NEW — set from JWT in Lobby for scheduled meetings
  lobbyCompleted: boolean;
  setMicState: (enabled: boolean) => void;
  setVideoState: (enabled: boolean) => void;
  toggleMic: () => void;
  toggleVideo: () => void;
  setDisplayName: (name: string) => void;
  setEmail: (email: string) => void; // NEW
  setRole: (role: 'host' | 'participant' | 'interviewer' | null) => void; // NEW
  setInterviewId: (id: string | null) => void; // NEW
  setLobbyCompleted: (val: boolean) => void;
  setSelectedAudioDeviceId: (id: string) => void;
  setSelectedVideoDeviceId: (id: string) => void;
  setLastCallStats: (stats: CallStats | null) => void; // NEW
}

const MediaContext = createContext<MediaContextType | undefined>(undefined);

export const MediaProvider = ({ children }: { children: ReactNode }) => {
  // Helper to get Room ID from URL
  const getRoomIdFromUrl = () => {
    const match = window.location.pathname.match(/\/(?:room|lobby)\/([^\/]+)/);
    return match ? match[1] : null;
  };

  const getStoredState = <T,>(key: string, defaultValue: T): T => {
    try {
      const currentRoomId = getRoomIdFromUrl();
      if (!currentRoomId) return defaultValue;

      const saved = sessionStorage.getItem('pz_media_prefs');
      if (!saved) return defaultValue;

      const parsed = JSON.parse(saved);
      if (parsed.roomId !== currentRoomId) return defaultValue;

      return (parsed[key] ?? defaultValue) as T;
    } catch {
      return defaultValue;
    }
  };

  const [isMicOn, setIsMicOn] = useState(() => getStoredState('mic', true));
  const [isVideoOn, setIsVideoOn] = useState(() => getStoredState('video', true));

  const [displayName, setDisplayName] = useState('');
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState('');
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState('');
  const [lastCallStats, setLastCallStats] = useState<CallStats | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'host' | 'participant' | 'interviewer' | null>(null);
  const [interviewId, setInterviewIdInternal] = useState<string | null>(
    () => getStoredState<string | null>('interviewId', null)
  );
  const [lobbyCompleted, setLobbyCompletedInternal] = useState<boolean>(
    () => getStoredState<boolean>('lobbyCompleted', false)
  );

  const saveState = (updates: { mic?: boolean; video?: boolean; interviewId?: string | null; lobbyCompleted?: boolean }) => {
    const currentRoomId = getRoomIdFromUrl();
    if (!currentRoomId) return;

    // Only update if Room ID matches or we are starting new
    const newState = {
      roomId: currentRoomId,
      mic: updates.mic ?? isMicOn,
      video: updates.video ?? isVideoOn,
      interviewId: 'interviewId' in updates ? updates.interviewId : interviewId,
      lobbyCompleted: 'lobbyCompleted' in updates ? updates.lobbyCompleted : lobbyCompleted,
    };

    sessionStorage.setItem('pz_media_prefs', JSON.stringify(newState));
  };

  const setInterviewId = (id: string | null) => {
    setInterviewIdInternal(id);
    saveState({ interviewId: id });
  };

  const setLobbyCompleted = (val: boolean) => {
    setLobbyCompletedInternal(val);
    saveState({ lobbyCompleted: val });
  };

  const setMicState = (enabled: boolean) => {
    setIsMicOn(enabled);
    saveState({ mic: enabled });
  };

  const setVideoState = (enabled: boolean) => {
    setIsVideoOn(enabled);
    saveState({ video: enabled });
  };

  const toggleMic = () => {
    setIsMicOn((prev: boolean) => {
      const newState = !prev;
      saveState({ mic: newState });
      return newState;
    });
  };

  const toggleVideo = () => {
    setIsVideoOn((prev: boolean) => {
      const newState = !prev;
      saveState({ video: newState });
      return newState;
    });
  };

  return (
    <MediaContext.Provider value={{
      isMicOn,
      isVideoOn,
      displayName,
      selectedAudioDeviceId,
      selectedVideoDeviceId,
      lastCallStats, // NEW
      email, // NEW
      role, // NEW
      interviewId, // NEW
      lobbyCompleted,
      setMicState,
      setVideoState,
      toggleMic,
      toggleVideo,
      setDisplayName,
      setEmail, // NEW
      setRole, // NEW
      setInterviewId, // NEW
      setLobbyCompleted,
      setSelectedAudioDeviceId,
      setSelectedVideoDeviceId,
      setLastCallStats // NEW
    }}>
      {children}
    </MediaContext.Provider>
  );
};

export const useMedia = () => {
  const context = useContext(MediaContext);
  if (context === undefined) {
    throw new Error('useMedia must be used within a MediaProvider');
  }
  return context;
};
